import React, { useMemo, useEffect, useRef, useState } from "react";
import { Spin } from "antd";

const TEMPLATE_SIZES = {
  "2.6": { width: 148, height: 76 },
};

const TemplatePreview = ({ templateName, sourceCode, width, height }) => {
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);

  const matchedSize = useMemo(() => {
    if (!templateName) return null;
    const key = Object.keys(TEMPLATE_SIZES).find(k =>
      templateName.toLowerCase().includes(k.toLowerCase())
    );
    return key ? TEMPLATE_SIZES[key] : null;
  }, [templateName]);

  const resolvedWidth = width ?? matchedSize?.width ?? 200;
  const resolvedHeight = height ?? matchedSize?.height ?? 100;

  const svgHtml = useMemo(() => {
    if (!sourceCode) return null;

    // 1) basic cleanup (your originals)
    let cleaned = sourceCode.replace(/\\"/g, '"')
      .replace(/>\s*n\s+/g, ">")
      .replace(/\s*n\s+</g, "<")
      .replace(/xlink:href=/g, "href=")
      .replace(/href="images\/barcode\.png"/g, 'href="/images/barcode.png"');

    // 2) Parse SVG and normalize <svg> attributes
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(cleaned, "image/svg+xml");
      const svg = doc.documentElement;

      if (svg && svg.tagName.toLowerCase() === "svg") {
        // grab existing width/height if present
        const rawW = svg.getAttribute("width");
        const rawH = svg.getAttribute("height");

        // extract numeric part (e.g. "296px" -> 296)
        const toNum = (val) => {
          if (!val) return null;
          const m = String(val).match(/([\d.]+)/);
          return m ? parseFloat(m[1]) : null;
        };

        let viewW = toNum(svg.getAttribute("viewBox")?.split(/\s+/)?.[2]);
        let viewH = toNum(svg.getAttribute("viewBox")?.split(/\s+/)?.[3]);

        // If no viewBox width/height, try from explicit width/height
        if (!viewW || !viewH) {
          const wFromAttr = toNum(rawW);
          const hFromAttr = toNum(rawH);
          if (wFromAttr && hFromAttr) {
            viewW = wFromAttr;
            viewH = hFromAttr;
          }
        }

        // If still missing, fall back to the resolved wrapper size
        if (!viewW || !viewH) {
          viewW = resolvedWidth;
          viewH = resolvedHeight;
        }

        // Ensure we have a viewBox
        if (!svg.getAttribute("viewBox") && viewW && viewH) {
          svg.setAttribute("viewBox", `0 0 ${viewW} ${viewH}`);
        }

        // Remove fixed size so the SVG can scale within the container
        svg.removeAttribute("width");
        svg.removeAttribute("height");

        // Make sure it scales to the wrapper
        // (width/height 100% via inline style, keep aspect ratio)
        const style = svg.getAttribute("style") || "";
        if (!/width\s*:/.test(style)) {
          svg.setAttribute("style", `${style};width:100%;height:100%;display:block;`);
        } else {
          // ensure height is also 100%
          if (!/height\s*:/.test(style)) {
            svg.setAttribute("style", `${style};height:100%;display:block;`);
          }
        }
        if (!svg.getAttribute("preserveAspectRatio")) {
          svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
        }

        // Serialize back to string
        const serializer = new XMLSerializer();
        cleaned = serializer.serializeToString(doc);
      }
    } catch {
      // If parsing fails, we’ll fall back to the cleaned string as-is
    }

    return cleaned;
  }, [sourceCode, resolvedWidth, resolvedHeight]);

  useEffect(() => {
    if (!containerRef.current) return;

    const images = containerRef.current.querySelectorAll("image");
    if (images.length === 0) {
      setLoading(false);
      return;
    }

    let loaded = 0;
    const handleDone = () => {
      loaded += 1;
      if (loaded >= images.length) setLoading(false);
    };

    images.forEach((img) => {
      img.addEventListener("load", handleDone);
      img.addEventListener("error", handleDone);
    });

    return () => {
      images.forEach((img) => {
        img.removeEventListener("load", handleDone);
        img.removeEventListener("error", handleDone);
      });
    };
  }, [svgHtml]);

  if (!svgHtml) return null;

  return (
    <Spin spinning={loading}>
      <div
        ref={containerRef}
        style={{
          width: resolvedWidth,
          height: resolvedHeight,
          padding: "5px",
          border: "1px solid #000",
          borderRadius: "5px",
          display: "inline-block",
          overflow: "hidden",
          lineHeight: 0,
          boxSizing: "content-box",
        }}
        dangerouslySetInnerHTML={{ __html: svgHtml }}
        aria-label="Template preview"
        role="img"
      />
    </Spin>
  );
};

export default TemplatePreview;